import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import * as global from '../data/global';
import { MuiMaterials } from '../@index.js';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';

const BinPicker = (props) => {
    const bins = global.gw.selectedSite.data?.bins || [];
    const cables = global.gw.selectedSite.cables || [];
    
    const [isArrayChanged, setIsArrayChanged] = useState(false);
    const [selectedNames, setSelectedNames] = useState(bins.map(b => { return b.name; }));
    const [allChecked, setAllChecked] = useState(bins.slice().fill(true));
    const [allCheckboxChecked, setAllCheckboxChecked] = useState(true);
    const [forceUpdate, setForceUpdate] = useState(0);

    // Helper function to get theme-aware colors (reads directly from global.gw.theme)
    const getThemeColor = useCallback((lightColor, darkColor) => {
        if (global.gw.theme === 'light') {
            return lightColor;
        }
        return darkColor;
    }, []);

    // Listen for theme changes - only trigger re-render when theme actually changes
    useEffect(() => {
        const handleThemeChange = () => {
            setForceUpdate(prev => prev + 1);
        };
        window.addEventListener('themeChanged', handleThemeChange);
        return () => window.removeEventListener('themeChanged', handleThemeChange);
    }, []);
    
    const binTriggerRef = useRef(null);
    const binLabelRef = useRef(null);
    const binListRef = useRef(null);
    const binPanelRef = useRef(null);
    const [menuOpen, setMenuOpen] = useState(false);

    const positionBinListPanel = useCallback(() => {
        const trigger = binTriggerRef.current;
        const label = binLabelRef.current;
        const panel = binPanelRef.current;
        if (!trigger || !panel) return;
        const anchor = label || trigger;
        const rect = anchor.getBoundingClientRect();
        const maxHeight = Math.max(120, window.innerHeight - rect.bottom - 12);
        panel.style.top = `${rect.bottom + 4}px`;
        panel.style.left = `${rect.left}px`;
        panel.style.minWidth = `${Math.max(rect.width, 160)}px`;
        panel.style.width = "max-content";
        panel.style.maxWidth = `${Math.max(160, window.innerWidth - rect.left - 8)}px`;
        panel.style.maxHeight = `${maxHeight}px`;
    }, []);

    const closeBinList = useCallback(() => {
        const binList = binListRef.current;
        if (!binList || binList.style.display === "none") return;
        binList.style.display = "none";
        setMenuOpen(false);
    }, []);

    const toggleBinList = useCallback(() => {
        const binList = binListRef.current;
        if (!binList) return;
        if (binList.style.display === "none" || binList.style.display === "") {
            binList.style.display = "unset";
            setMenuOpen(true);
            positionBinListPanel();
        } else {
            binList.style.display = "none";
            setMenuOpen(false);
        }
    }, [positionBinListPanel]);

    useEffect(() => {
        const handlePointerDown = (event) => {
            const binList = binListRef.current;
            if (!binList || binList.style.display === "none") return;
            const target = event.target;
            if (binTriggerRef.current?.contains(target)) return;
            if (target.closest?.(".binListPanel")) return;
            closeBinList();
        };
        document.addEventListener("mousedown", handlePointerDown);
        return () => document.removeEventListener("mousedown", handlePointerDown);
    }, [closeBinList]);

    const selectionSummary = useMemo(() => {
        if (!bins.length) return "None";
        const checkedCount = allChecked.filter(Boolean).length;
        if (checkedCount === 0) return "None";
        if (checkedCount === bins.length) return "All";
        return `${checkedCount} selected`;
    }, [bins.length, allChecked]);

    const fieldSx = useMemo(() => ({
        width: "auto",
        minWidth: 140,
        maxWidth: 200,
        flex: "0 0 auto",
        '& .MuiOutlinedInput-root': {
            color: getThemeColor('#333333', '#f4cc8e'),
            backgroundColor: getThemeColor('#ffffff', '#2d2d2d'),
            fontSize: '14px',
            cursor: 'pointer',
            '& fieldset': { borderColor: getThemeColor('#cccccc', 'rgba(244,204,142,0.5)') },
            '&:hover fieldset': { borderColor: getThemeColor('#666666', '#f4cc8e') },
            '&.Mui-focused fieldset': { borderColor: getThemeColor('#666666', '#f4cc8e') },
        },
        '& .MuiInputLabel-root': { color: getThemeColor('#666666', '#a8abb1'), fontSize: '14px' },
        '& .MuiInputLabel-root.Mui-focused': { color: getThemeColor('#333333', '#f4cc8e') },
        '& .MuiInputBase-input': {
            color: getThemeColor('#333333', '#f4cc8e'),
            fontSize: '14px',
            cursor: 'pointer',
            paddingTop: '8.5px',
            paddingBottom: '8.5px',
        },
        '& .MuiSvgIcon-root': { color: getThemeColor('#666666', '#a8abb1') },
    }), [getThemeColor, forceUpdate]);

    // Always show all bins (don't filter them out)
    const filteredBins = bins;
    
    // Update allCheckboxChecked when bins change
    useEffect(() => {
        const allBinsChecked = bins.length > 0 && bins.every((bin, idx) => {
            return allChecked[idx] === true;
        });
        setAllCheckboxChecked(allBinsChecked);
        if (props.onAllCheckboxChange) {
            props.onAllCheckboxChange(allBinsChecked);
        }
    }, [bins, allChecked]);
    
    // Sync with cable picker's "All" checkbox
    useEffect(() => {
        if (props.syncAllCheckbox !== undefined && props.syncAllCheckbox !== allCheckboxChecked) {
            setAllCheckboxChecked(props.syncAllCheckbox);
        }
    }, [props.syncAllCheckbox]);
    
    // Track if this is the initial mount
    const isInitialMount = useRef(true);
    
    // Sync allChecked state with selectedBins from parent (ScanWindow)
    // Bins that are in selectedBins must be checked
    // Bins not in selectedBins should be unchecked UNLESS all their cables are selected (auto-select)
    useEffect(() => {
        if (bins.length === 0) return;
        
        const selectedBinIds = new Set(props.selectedBins || []);
        const selectedCableIds = new Set(props.selectedCables || []);
        let tempChecked = [...allChecked];
        let changed = false;
        
        bins.forEach((bin, idx) => {
            const binId = bin.binId;
            const isInSelectedBins = selectedBinIds.has(binId);
            
            if (isInSelectedBins && !tempChecked[idx]) {
                // Bin is in selectedBins but not checked
                // Only check it if all its cables are selected
                // If not all cables are selected, keep it unchecked (will be handled by auto-select logic)
                if (bin.cableList && bin.cableList.length > 0) {
                    const allCablesInBinSelected = bin.cableList.every(cable => selectedCableIds.has(cable.id));
                    if (allCablesInBinSelected) {
                        // All cables selected, so check the bin
                        tempChecked[idx] = true;
                        changed = true;
                    }
                    // If not all cables are selected, keep it unchecked (auto-select will keep it unchecked)
                } else {
                    // No cables in bin, so check it (bin is in selectedBins)
                    tempChecked[idx] = true;
                    changed = true;
                }
            } else if (!isInSelectedBins && tempChecked[idx]) {
                // Bin is not in selectedBins but is checked - check if we should keep it checked
                // Only keep it checked if all its cables are selected (auto-select case)
                if (bin.cableList && bin.cableList.length > 0) {
                    const allCablesInBinSelected = bin.cableList.every(cable => selectedCableIds.has(cable.id));
                    if (!allCablesInBinSelected) {
                        // Not all cables selected, so uncheck the bin
                        tempChecked[idx] = false;
                        changed = true;
                    }
                    // If all cables are selected, keep it checked (auto-select case)
                } else {
                    // No cables in bin, so uncheck it
                    tempChecked[idx] = false;
                    changed = true;
                }
            }
        });
        
        if (changed) {
            setAllChecked(tempChecked);
            
            // Update selectedNames based on checked bins
            const checkedBinNames = bins.filter((bin, idx) => tempChecked[idx]).map(b => b.name);
            setSelectedNames(checkedBinNames);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.selectedBins, props.selectedCables]);
    
    // Auto-select/deselect bins based on whether all their cables are selected
    // This only applies to bins NOT in selectedBins (user hasn't manually selected them)
    // If a bin is in selectedBins, it should remain checked regardless of cable selection
    // IMPORTANT: Only auto-check bins that are currently unchecked when all their cables become selected
    // Do NOT auto-check bins that were never selected by the user when another bin is deselected
    useEffect(() => {
        if (!props.selectedCables || bins.length === 0) return;
        
        // Skip auto-select on initial mount to keep all bins selected on page load
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }
        
        const selectedCableIds = new Set(props.selectedCables);
        const selectedBinIds = new Set(props.selectedBins || []);
        let tempChecked = [...allChecked];
        let changed = false;
        
        // Check each bin - if all its cables are selected, check the bin; otherwise uncheck it
        bins.forEach((bin, idx) => {
            if (!bin.cableList || bin.cableList.length === 0) return;
            
            const binId = bin.binId;
            const isInSelectedBins = selectedBinIds.has(binId);
            
            // Check if all cables in this bin are selected
            const allCablesInBinSelected = bin.cableList.every(cable => selectedCableIds.has(cable.id));
            
            if (isInSelectedBins) {
                // Bin is manually selected (in selectedBins)
                // If not all cables are selected, uncheck it visually (but don't remove from selectedBins)
                // We don't call props.selected(binId, false) because that would deselect all cables in the bin
                // The sync logic will keep it unchecked as long as not all cables are selected
                if (!allCablesInBinSelected && tempChecked[idx]) {
                    // Not all cables selected, but bin is checked - uncheck it visually only
                    // DO NOT call props.selected(binId, false) here - it would deselect all cables
                    tempChecked[idx] = false;
                    changed = true;
                }
                // If all cables are selected, keep it checked (it's already in selectedBins)
            } else {
                // Bin is NOT manually selected (not in selectedBins)
                // Only auto-check if all cables are selected AND the bin is currently unchecked
                // This prevents auto-checking bins that were never selected when another bin is deselected
                if (allCablesInBinSelected && !tempChecked[idx]) {
                    // All cables selected, but bin is unchecked - check it (visual only, don't call props.selected)
                    // This only happens when user manually selects all cables in a bin
                    tempChecked[idx] = true;
                    changed = true;
                } else if (!allCablesInBinSelected && tempChecked[idx]) {
                    // Not all cables selected, but bin is checked - uncheck it (visual only)
                    // This happens when user deselects some cables in a bin
                    tempChecked[idx] = false;
                    changed = true;
                }
            }
        });
        
        if (changed) {
            setAllChecked(tempChecked);
            
            // Update selectedNames based on checked bins
            const checkedBinNames = bins.filter((bin, idx) => tempChecked[idx]).map(b => b.name);
            setSelectedNames(checkedBinNames);
            
            // DON'T trigger bin selection callbacks when auto-checking/unchecking based on cables
            // This prevents circular updates where:
            // 1. Cable deselected → bin auto-unchecks → binSelected called → all cables in bin unchecked
            // Only update the visual state, don't trigger the callback that would affect cables
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.selectedCables]);

    const theme = useMemo(() => {
        const isLight = global.gw.theme === 'light';
        return MuiMaterials.createTheme({
            components: {
                MuiInputLabel: {
                    styleOverrides: {
                        root: {
                            color: isLight ? '#000000' : '#f4cc8e',
                            fontSize: "12px",
                            ...(isLight && {
                                '&:not(.MuiInputLabel-shrink)': {
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    left: '14px',
                                },
                            }),
                        },
                        shrink: {
                            top: isLight ? '8px' : undefined,
                        },
                    }
                },
                MuiButton: {
                    styleOverrides: {
                        root: {
                            fontSize: "12px",
                            background: isLight ? '#e0e0e0' : '#2d2d2d',
                            color: isLight ? '#333333' : 'rgb(244, 204, 142)',
                        }
                    }
                },
                MuiIconButton: {
                    styleOverrides: {
                        sizeMedium: {
                            color: isLight ? '#333333' : 'rgb(244, 204, 142)'
                        }
                    }
                },
            },
        });
    }, [forceUpdate]);

    useEffect(() => {
        //props.selectedCables = selectedNames;



    }, []);

    return (
        <MuiMaterials.Box
            style={props.windowStyle}
            sx={{ display: "flex", alignItems: "center", width: "auto", flex: "0 0 auto", minWidth: 0 }}
        >
            <MuiMaterials.FormControl
                ref={binTriggerRef}
                size="small"
                variant="outlined"
                sx={fieldSx}
            >
                <MuiMaterials.InputLabel id="scan-bins-label" shrink>
                    Bins
                </MuiMaterials.InputLabel>
                <MuiMaterials.OutlinedInput
                    ref={binLabelRef}
                    label="Bins"
                    notched
                    readOnly
                    value={selectionSummary}
                    onClick={toggleBinList}
                    endAdornment={(
                        <MuiMaterials.Box
                            component="span"
                            sx={{ display: "flex", alignItems: "center", pointerEvents: "none", mr: 0.25 }}
                        >
                            {menuOpen ? <ArrowDropUpIcon fontSize="small" /> : <ArrowDropDownIcon fontSize="small" />}
                        </MuiMaterials.Box>
                    )}
                    inputProps={{
                        "aria-labelledby": "scan-bins-label",
                        "aria-expanded": menuOpen,
                        "aria-haspopup": "listbox",
                    }}
                />
            </MuiMaterials.FormControl>
            <div
                ref={binListRef}
                className="binList"
                style={{
                    position: "fixed",
                    top: "116px",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 1100,
                    display: "none",
                }}
            >
                <div
                    className="binListBackdrop"
                    style={{
                        position: "absolute",
                        inset: 0,
                    }}
                    onMouseDown={closeBinList}
                />
                <div
                    ref={binPanelRef}
                    className="binListPanel hide-scrollbar"
                    style={{
                        position: "fixed",
                        zIndex: 1,
                        overflow: "auto",
                        background: getThemeColor('#ffffff', '#2d2d2d'),
                        border: `1px solid ${getThemeColor('#cccccc', 'rgba(244,204,142,0.5)')}`,
                        borderRadius: "4px",
                        boxShadow: getThemeColor(
                            "0px 5px 15px rgba(0,0,0,0.12)",
                            "0px 5px 15px rgba(0,0,0,0.45)",
                        ),
                        padding: "8px 12px 8px 8px",
                        boxSizing: "border-box",
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                >
                <MuiMaterials.ThemeProvider theme={theme} key={`binpicker-theme-${global.gw.theme}-${forceUpdate}`}>
                    <div>
                        <MuiMaterials.FormControlLabel
                            sx={{
                                color: getThemeColor('#333333', '#f4cc8e'),
                                margin: "0px",
                                width: "fit-content",
                                maxWidth: "100%",
                            }}
                            control={<MuiMaterials.Checkbox
                                key={`binpicker-all-checkbox-${global.gw.theme}-${forceUpdate}`}
                                checked={props.allCheckboxChecked !== undefined ? props.allCheckboxChecked : allCheckboxChecked}
                                sx={{
                                    color: getThemeColor('#333333', '#f4cc8e'),
                                    '&.Mui-checked': {
                                        color: getThemeColor('#333333', '#f4cc8e'),
                                    },
                                    padding: "1px 0",
                                }}
                                onChange={e => {
                                    let tempChecked = [...allChecked];
                                    // Update checked state for all bins
                                    for (let i = 0; i < bins.length; i++) {
                                        tempChecked[i] = e.target.checked;
                                    }
                                    setAllChecked(tempChecked);
                                    const newChecked = e.target.checked;
                                    setAllCheckboxChecked(newChecked);
                                    // Sync with parent and cable picker
                                    if (props.onAllCheckboxChange) {
                                        props.onAllCheckboxChange(newChecked);
                                    }
                                    if (e.target.checked) {
                                        // Add all bin names
                                        const allBinNames = bins.map(b => b.name);
                                        setSelectedNames(allBinNames);
                                    }
                                    else {
                                        // Remove all bin names
                                        setSelectedNames([]);
                                    }
                                    if (props && props.selectedAll) {
                                        props.selectedAll(e.target.checked);
                                    }
                                    e.preventDefault = true;
                                    return false;
                                }}
                            />}
                            label={"All"}
                            id={"checkAllBins"}
                        />
                    </div>
                <MuiMaterials.FormGroup sx={{ alignItems: "flex-start" }}>
                    {bins.map((bin, b) => {
                        // Use the index directly since we're mapping over bins
                        const originalIndex = b;
                        return (
                        <MuiMaterials.FormControlLabel
                            key={`binpicker-bin-${bin.binId}-${global.gw.theme}-${forceUpdate}`}
                            sx={{
                                color: getThemeColor('#333333', '#f4cc8e'),
                                margin: "0px",
                                width: "fit-content",
                                maxWidth: "100%",
                            }}
                            control={<MuiMaterials.Checkbox
                                className="chbBin"
                                defaultChecked
                                checked={originalIndex >= 0 ? allChecked[originalIndex] : false}
                                sx={{
                                    color: getThemeColor('#333333', '#f4cc8e'),
                                    '&.Mui-checked': {
                                        color: getThemeColor('#333333', '#f4cc8e'),
                                    },
                                    padding: "1px 0",
                                }}
                                onChange={e => {
                                    let tempChecked = [...allChecked];
                                    tempChecked[originalIndex] = e.target.checked;
                                    setAllChecked(tempChecked);
                                    
                                    // Update allCheckboxChecked based on whether all bins are checked
                                    const allBinsChecked = bins.every((bin, idx) => {
                                        return tempChecked[idx] === true;
                                    });
                                    setAllCheckboxChecked(allBinsChecked);
                                    
                                    let tempBinNames = [...selectedNames];
                                    if (e.target.checked) {
                                        if (!tempBinNames.includes(bin.name)) {
                                            tempBinNames.push(bin.name);
                                        }
                                    }
                                    else {
                                        for (let i = 0; i < tempBinNames.length; i++) {
                                            let bn = tempBinNames[i];
                                            if (bn === bin.name) {
                                                tempBinNames.splice(i, 1);
                                                break;
                                            }
                                        }
                                    }
                                    setSelectedNames(tempBinNames);
                                    setIsArrayChanged(!isArrayChanged);

                                    if (props && props.selected) {
                                        props.selected(bin.binId, e.target.checked);
                                    }

                                }}
                            />}
                            label={bin.name}
                            inputProps={{ 'aria-label': 'controlled' }}
                        />
                        );
                    })}
                </MuiMaterials.FormGroup>
            </MuiMaterials.ThemeProvider>
                </div>
            </div>
        </MuiMaterials.Box>
    );
}
export default BinPicker;